import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Header, Footer, PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ShieldWarning, ArrowsClockwise, TrendUp, Wallet, Crown, UserCircle,
  Copy, ArrowRight, Trophy, ChatCircleDots, LockKey, Calendar, VideoCamera,
} from "@phosphor-icons/react";

/**
 * AdminWhalesPage
 *
 * "Whales Finder" — super-admin-only ranking of the top-paying customers
 * across every revenue source (wallet top-ups, Razorpay tool unlocks,
 * 20-year reports, Personal Zoom bookings). Everything normalised to
 * INR using the same USD→INR rate as the LLM-usage dashboard.
 */

const WINDOWS = [
  { key: "7d",  label: "Last 7 d" },
  { key: "30d", label: "Last 30 d" },
  { key: "90d", label: "Last 90 d" },
  { key: "all", label: "All time" },
];

const SOURCE_META = {
  wallet_topup:         { label: "Top-ups",   Icon: Wallet,        color: "text-emerald-700 bg-emerald-100 border-emerald-300" },
  tool_unlock_razorpay: { label: "Tool ×",    Icon: LockKey,       color: "text-violet-700 bg-violet-100 border-violet-300" },
  twenty_year:          { label: "20-Yr",     Icon: Calendar,      color: "text-sky-700 bg-sky-100 border-sky-300" },
  personal_zoom:        { label: "Zoom",      Icon: VideoCamera,   color: "text-amber-700 bg-amber-100 border-amber-300" },
};

function fmtInr(n, digits = 0) {
  if (n == null || isNaN(n)) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { year:"numeric", month:"short", day:"numeric" }); }
  catch { return iso; }
}

function fmtRel(iso) {
  if (!iso) return "";
  try {
    const diffMin = (Date.now() - new Date(iso).getTime()) / 60000;
    if (diffMin < 60) return `${Math.round(diffMin)} m ago`;
    if (diffMin < 60*24) return `${(diffMin/60).toFixed(1)} h ago`;
    if (diffMin < 60*24*30) return `${(diffMin/(60*24)).toFixed(1)} d ago`;
    return `${(diffMin/(60*24*30)).toFixed(1)} mo ago`;
  } catch { return ""; }
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(String(value)); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      className="ml-1 inline-flex items-center opacity-60 hover:opacity-100 transition-opacity"
      title="Copy"
      data-testid="copy-btn"
    >
      <Copy size={11} /> {copied && <span className="ml-0.5 text-[9px] text-emerald-700">✓</span>}
    </button>
  );
}

export default function AdminWhalesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [window, setWindow] = useState("all");
  const [limit, setLimit] = useState(15);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isSuperAdmin = !!user?.is_super_admin;

  const refresh = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/admin/whales", { params: { window, limit } });
      setData(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to load whales";
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  }, [isSuperAdmin, window, limit]);

  useEffect(() => { refresh(); }, [refresh]);

  const topSpend = useMemo(() => {
    if (!data?.whales?.length) return 0;
    return Math.max(...data.whales.map((w) => w.total_paid_inr || 0));
  }, [data]);

  if (!isSuperAdmin) {
    return (
      <PageShell>
        <Header />
        <div className="max-w-3xl mx-auto px-6 pt-20 pb-24 text-center" data-testid="whales-denied">
          <ShieldWarning size={48} weight="duotone" className="text-rose-400 mx-auto mb-4" />
          <h1 className="text-2xl font-serif-display text-stone-800 mb-2">Super-admin only</h1>
          <p className="text-stone-600">The Whales Finder ranks your highest-paying customers so you know who to nurture.</p>
          <button onClick={() => navigate("/")} className="mt-6 px-5 py-2 rounded-full bg-stone-800 text-white hover:bg-stone-900" data-testid="back-home">Back to home</button>
        </div>
        <Footer />
      </PageShell>
    );
  }

  const t = data?.totals || {};

  return (
    <PageShell>
      <Header />
      <main className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="admin-whales-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Super Admin</div>
            <h1 className="text-3xl sm:text-4xl font-serif-display text-stone-800 flex items-center gap-2">
              <Trophy size={28} weight="fill" className="text-amber-500" /> Whales Finder
            </h1>
            <p className="text-stone-600 mt-1 text-sm max-w-2xl">
              Top-paying customers across every revenue source — wallet top-ups, Razorpay-paid tool unlocks, 20-year reports and Personal Zoom bookings. Know who to send the personalised follow-ups to.
            </p>
          </div>
          <button onClick={refresh} disabled={loading}
            className="rounded-full bg-stone-800 text-white text-sm px-4 py-1.5 inline-flex items-center gap-1.5 hover:bg-stone-900 disabled:opacity-60"
            data-testid="refresh-btn">
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <div className="flex gap-2 flex-wrap" data-testid="window-selector">
            {WINDOWS.map((w) => (
              <button key={w.key} onClick={() => setWindow(w.key)}
                className={`px-3.5 py-1.5 rounded-full text-sm border transition
                  ${window === w.key
                    ? "bg-stone-800 text-white border-stone-800"
                    : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"}`}
                data-testid={`window-${w.key}`}>
                {w.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-sm text-stone-600">
            <span>Show top</span>
            <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))}
              className="rounded-lg border border-stone-300 bg-white px-2.5 py-1.5"
              data-testid="limit-selector">
              {[5, 10, 15, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div className="mt-6 rounded-xl p-4 bg-rose-50 border border-rose-200 text-rose-900 text-sm" data-testid="whales-error">{error}</div>
        )}

        {/* KPI cards */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-cards">
          <KpiCard label="Total revenue" value={fmtInr(t.revenue_inr, 0)} sub="all sources" highlight testId="kpi-revenue" />
          <KpiCard label="From top listed"    value={fmtInr(t.revenue_shown_top_inr, 0)} sub={`${limit} whales`} testId="kpi-top-revenue" />
          <KpiCard label="Whale share"        value={`${t.top_share_pct != null ? t.top_share_pct.toFixed(1) : "—"} %`} sub="of total" testId="kpi-share" />
          <KpiCard label="Paying customers"   value={(t.unique_paying_customers ?? 0).toLocaleString("en-IN")} sub="unique users" testId="kpi-customers" />
        </div>

        {/* Whales table */}
        <section className="mt-8 rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="whales-table-panel">
          <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
            <h2 className="text-xl font-serif-display text-stone-800 flex items-center gap-2">
              <Crown size={20} weight="fill" className="text-amber-500" /> Ranked whales
            </h2>
            <div className="text-[11px] text-stone-500 uppercase tracking-wider">
              {data?.whales?.length || 0} shown
            </div>
          </div>

          {!data?.whales?.length ? (
            <div className="p-10 text-center text-stone-500 text-sm" data-testid="whales-empty">
              <TrendUp size={20} weight="duotone" className="mx-auto mb-2 text-stone-400" />
              No paying customers in this window yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="whales-table">
                <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium w-10">#</th>
                    <th className="text-left px-3 py-2 font-medium">Customer</th>
                    <th className="text-left px-3 py-2 font-medium">Total paid</th>
                    <th className="text-left px-3 py-2 font-medium">By source</th>
                    <th className="text-right px-3 py-2 font-medium">Txns</th>
                    <th className="text-right px-3 py-2 font-medium">Wallet now</th>
                    <th className="text-left px-3 py-2 font-medium">Last paid</th>
                    <th className="text-right px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {data.whales.map((w, i) => (
                    <WhaleRow key={w.user_id || i} w={w} rank={i + 1} maxSpend={topSpend} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="mt-6 text-[11px] text-stone-500" data-testid="disclaimer">
          Rate used: <span className="font-mono">₹{data?.usd_to_inr_rate?.toFixed(2) || "84.00"}</span> / USD ·
          Revenue includes: wallet top-ups (Razorpay paid), Razorpay-paid direct tool unlocks, 20-Year Prediction orders, Personal Zoom bookings.
          Chat billing is NOT counted here — those charges are internal wallet deductions, not fresh revenue.
        </div>
      </main>
      <Footer />
    </PageShell>
  );
}

function KpiCard({ label, value, sub, highlight, testId }) {
  return (
    <div className={`rounded-2xl border p-4 ${highlight ? "border-amber-300 bg-amber-50" : "border-stone-200 bg-white"}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`font-serif-display text-2xl mt-0.5 ${highlight ? "text-amber-800" : "text-stone-900"}`}>{value}</div>
      {sub && <div className="text-[11px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function WhaleRow({ w, rank, maxSpend }) {
  const pct = maxSpend > 0 ? Math.min(100, (w.total_paid_inr / maxSpend) * 100) : 0;
  const rankColor =
    rank === 1 ? "bg-amber-400 text-amber-950" :
    rank === 2 ? "bg-stone-300 text-stone-900" :
    rank === 3 ? "bg-orange-300 text-orange-950" :
                 "bg-stone-100 text-stone-700";
  return (
    <tr data-testid={`whale-row-${rank}`} className="hover:bg-amber-50/30">
      <td className="px-3 py-3 align-top">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${rankColor}`}>
          {rank}
        </div>
      </td>
      <td className="px-3 py-3 align-top min-w-[220px]">
        <div className="flex items-center gap-2">
          {w.picture
            ? <img src={w.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-stone-200" />
            : <UserCircle size={30} weight="duotone" className="text-stone-400" />}
          <div className="min-w-0">
            <div className="text-stone-900 font-medium truncate max-w-[210px]" title={w.name}>{w.name || "—"}</div>
            <div className="text-[11px] text-stone-500 truncate max-w-[210px]" title={w.email}>{w.email || <em>no email</em>}</div>
            <div className="text-[10px] text-stone-400 truncate">
              <code>{w.user_id?.slice(0, 24)}{w.user_id?.length > 24 ? "…" : ""}</code>
              <CopyBtn value={w.user_id} />
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3 align-top min-w-[160px]">
        <div className="text-stone-900 font-serif-display text-xl">{fmtInr(w.total_paid_inr, 0)}</div>
        <div className="mt-1 h-1.5 rounded-full bg-stone-100 overflow-hidden w-full max-w-[160px]">
          <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500" style={{ width: `${pct}%` }} />
        </div>
      </td>
      <td className="px-3 py-3 align-top">
        <div className="flex flex-wrap gap-1">
          {Object.entries(w.by_source || {})
            .filter(([, v]) => (v || 0) > 0)
            .map(([k, v]) => {
              const meta = SOURCE_META[k] || { label: k, Icon: TrendUp, color: "bg-stone-100 text-stone-700 border-stone-300" };
              const { Icon } = meta;
              return (
                <span key={k} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${meta.color}`} title={`${meta.label}: ${fmtInr(v, 0)}`}>
                  <Icon size={10} weight="fill" /> {meta.label} {fmtInr(v, 0)}
                </span>
              );
            })}
        </div>
      </td>
      <td className="px-3 py-3 align-top text-right whitespace-nowrap text-stone-700">
        <div>{w.total_txns}</div>
        <div className="text-[10px] text-stone-500 space-x-1">
          {(w.txns_by_source?.wallet_topup || 0) > 0 && <span>↑{w.txns_by_source.wallet_topup}</span>}
          {(w.txns_by_source?.tool_unlock_razorpay || 0) > 0 && <span>×{w.txns_by_source.tool_unlock_razorpay}</span>}
          {(w.txns_by_source?.twenty_year || 0) > 0 && <span>20y</span>}
          {(w.txns_by_source?.personal_zoom || 0) > 0 && <span>▶</span>}
        </div>
      </td>
      <td className="px-3 py-3 align-top text-right whitespace-nowrap">
        <div className="text-emerald-700 font-medium">{fmtInr(w.wallet_balance_inr, 0)}</div>
        {w.vip_credit_inr > 0 && <div className="text-[10px] text-amber-700">+{fmtInr(w.vip_credit_inr, 0)} VIP</div>}
      </td>
      <td className="px-3 py-3 align-top whitespace-nowrap text-stone-600">
        <div>{fmtDate(w.last_paid_at)}</div>
        <div className="text-[10px] text-stone-400">{fmtRel(w.last_paid_at)}</div>
      </td>
      <td className="px-3 py-3 align-top text-right whitespace-nowrap">
        {w.email && (
          <Link
            to={`/admin/customer-lookup?q=${encodeURIComponent(w.email)}`}
            className="text-xs text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"
            data-testid={`view-customer-${rank}`}
          >
            Payments <ArrowRight size={12} />
          </Link>
        )}
        {w.email && (
          <Link
            to={`/admin/wallet-deductions?q=${encodeURIComponent(w.email)}`}
            className="text-xs text-stone-600 hover:text-stone-900 inline-flex items-center gap-1 mt-1"
            data-testid={`view-deductions-${rank}`}
          >
            Deductions <ArrowRight size={12} />
          </Link>
        )}
      </td>
    </tr>
  );
}
